import { sortBy } from '@softline/core';
import { PageFilterItem } from '../types/page-filter-item';
import { FieldFilter, Filter } from '../../types/filter';
import { filterValueKey } from '../../helper/filter-value-equals.function';

interface PageFilterChipBase {
  id: string;
  item: PageFilterItem<any, any>;
  filter: FieldFilter<any>;
}

export type PageFilterChip =
  /** an in/notIn value holding exactly one element */
  | (PageFilterChipBase & { kind: 'value'; value: unknown })
  /** an in/notIn value holding several elements: "Title: N"; removing it clears the field */
  | (PageFilterChipBase & { kind: 'collapsed'; count: number })
  /** a scalar, range or operator-only value */
  | (PageFilterChipBase & { kind: 'single' })
  /** an active toggle item; removing it flips the toggle off */
  | (PageFilterChipBase & { kind: 'toggle' });

/**
 * The chips for the row below the menu bar: one chip per visible item with a committed
 * value. Pinned (`menuBar`) items show their value in their dropdown and are skipped
 * unless `options.pinned` asks for them too.
 */
export function buildPageFilterChips(
  items: PageFilterItem<any, any>[],
  getValue: (item: PageFilterItem<any, any>) => Filter<any> | null,
  isToggleActive: (item: PageFilterItem<any, any>) => boolean,
  options: { pinned: boolean } = { pinned: false }
): PageFilterChip[] {
  const chips: PageFilterChip[] = [];
  for (const item of sortBy(items, 'order')) {
    if ((item.menuBar && !options.pinned) || (item.visible && !item.visible())) continue;
    const filter = getValue(item);
    if (!filter || filter.type === 'conditions') continue;
    // two items may share a field (toggle + regular), so the id is keyed by item position
    const prefix = `${items.indexOf(item)}:${String(item.field)}`;

    if (item.toggle) {
      if (isToggleActive(item)) chips.push({ kind: 'toggle', id: prefix, item, filter });
      continue;
    }
    const value = (filter as { value?: unknown }).value;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      // several values → "Title: N" instead of one chip per value (a pinned multi select shows the same count)
      const distinct = new Set(value.map((element) => filterValueKey(item.config, element)));
      if (distinct.size > 1) {
        chips.push({ kind: 'collapsed', id: prefix, item, filter, count: distinct.size });
        continue;
      }
      chips.push({ kind: 'value', id: `${prefix}:${[...distinct][0]}`, item, filter, value: value[0] });
      continue;
    }
    chips.push({ kind: 'single', id: prefix, item, filter });
  }
  return chips;
}
